import { useEffect, useState } from 'react';

import { ApiAndIntegrationsDropdown } from '#/components/ApiAndIntegrationsDropdown/ApiAndIntegrationsDropdown';
import { DesktopSearchBar } from '#/components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from '#/components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from '#/components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from '#/components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from '#/components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { FindAJobDropdown } from '#/components/FindAJobDropdown/FindAJobDropdown';
import { Icon } from '#/components/Icon/Icon';
import {
  LocationRadiusSelect,
  type LocationRadiusSelectHandle,
} from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { MobileSearchMenu } from '#/components/MobileSearchMenu/MobileSearchMenu';
import { ParticipateDropdown } from '#/components/ParticipateDropdown/ParticipateDropdown';
import { PostOnIdealistDropdown } from '#/components/PostOnIdealistDropdown/PostOnIdealistDropdown';
import { RouteLoadingProgressBar } from '#/components/RouteLoadingProgressBar/RouteLoadingProgressBar';
import { VolunteerDropdown } from '#/components/VolunteerDropdown/VolunteerDropdown';
import { CartDropdownButtonContainer } from '#/containers/CartDropdownButton/CartDropdownButtonContainer';
import { FlashMessageContainer } from '#/containers/FlashMessageContainer';
import { MyAccountDropdownContainerLoadable } from '#/containers/MyAccountDropdown/MyAccountDropdownContainerLoadable';
import { NotificationsDropdownContainerLoadable } from '#/containers/NotificationsDropdown/NotificationsDropdownContainerLoadable';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useIsClient } from '#/hooks/useIsClient';
import { useLogin } from '#/hooks/useLogin';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { authSignupRoute } from '#/modules/auth/routing/auth.routes';
import { SearchRefinementsSwitchLoadable } from '#/modules/search/components/Refinements/SearchRefinementsSwitchLoadable';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { searchGetVolopsRoutePath } from '#/modules/search/volops/routing/searchGetVolopsRoutePath';
import { homeRoute } from '#/routing/routes';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';

import { LayoutHeaderButton } from './Button/LayoutHeaderButton';
import {
  LayoutHeaderApiAndIntegrationsDropdownHolder,
  LayoutHeaderAuthHolder,
  LayoutHeaderAuthLink,
  LayoutHeaderBottomSectionHolder,
  LayoutHeaderCartDropdownButtonHolder,
  LayoutHeaderDesktopDonateLinkHolder,
  LayoutHeaderDesktopSearchHolder,
  LayoutHeaderHolder,
  LayoutHeaderInnerHolder,
  LayoutHeaderLeft,
  LayoutHeaderLinksHolder,
  LayoutHeaderLogoLink,
  LayoutHeaderMobileSearchHolder,
  LayoutHeaderMyAccountHolder,
  LayoutHeaderNotificationsDropdownHolder,
  LayoutHeaderProgressBarHolder,
  LayoutHeaderRight,
  LayoutHeaderSignupLinkHolder,
  LayoutHeaderTopSectionHolder,
} from './LayoutHeader.styled';
import { useLayoutHeaderSearch } from './useLayoutHeaderSearch';

const DONATE_BUTTON_DETAILS = {
  en: {
    text: 'Donate',
    href: 'https://giving.classy.org/campaign/673245/donate',
  },
  pt: {
    text: 'Doar',
    href: '/pt/doacao',
  },
  es: {
    text: 'Haz una Donación',
    href: '/es/dona',
  },
} as const satisfies Record<I18nLocale, { text: string; href: string }>;

const IDEALIST_DAYS_PATH = {
  en: '/en/days/get-involved',
  es: '/es/dias/participa',
  pt: '/pt/days/participe',
};

type Props = {
  isSearchPage: boolean;
  isSearchLandingPage: boolean;
  defaultSearchType: SearchType;
  showDesktopSearch: boolean;
  showSearchRefinements: boolean;
  searchFromPageType: SearchFromPageType | undefined;
};

export function LayoutHeader({
  isSearchPage,
  isSearchLandingPage,
  defaultSearchType,
  showDesktopSearch,
  showSearchRefinements,
  searchFromPageType,
}: Props) {
  const { i18n, locale } = useI18n();
  const { user } = useUser();

  const { environment } = useEnvironment();
  // Note: once we migrate to horizon we can remove this check as `environment`
  // is loaded during SSR and always present
  const isUserLoaded = Boolean(environment);

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  const [withScrolledTransition, setWithScrolledTransition] =
    useState(isSearchPage);
  useEffect(() => {
    if (isSearchPage === withScrolledTransition) return;
    setWithScrolledTransition(isSearchPage);
  }, [isSearchPage, withScrolledTransition]);

  const { loginLink, onClickLogin } = useLogin();

  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({ isSearchLandingPage });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isSearchPage
    ? { type: 'controlled', onChange: setSearchUrlRadius, radius }
    : { type: 'uncontrolled', radius };

  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType,
    isSearchPage,
    isSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  return (
    <LayoutHeaderHolder data-qa-id="page-header" $isInSearchPage={isSearchPage}>
      <LayoutHeaderInnerHolder>
        <LayoutHeaderTopSectionHolder>
          <LayoutHeaderLeft>
            <LayoutHeaderLogoLink
              to={homeRoute.with({ lang: locale })}
              title="Idealist"
              onClick={() => trackClicked('Idealist Logo Header')}
              qaId="header-logo-link"
            >
              <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
            </LayoutHeaderLogoLink>

            <LayoutHeaderLinksHolder $isInSearchPage={isSearchPage}>
              {locale === I18nLocale.ES || locale === I18nLocale.PT ? (
                <ParticipateDropdown
                  renderTrigger={({ triggerText, triggerHref }) => (
                    <LayoutHeaderButton
                      data={
                        isDesktop
                          ? {
                              type: 'link',
                              href: triggerHref,
                              onClick: () => {
                                trackClicked('Participate', {
                                  context: 'Header',
                                });
                              },
                            }
                          : { type: 'inert' }
                      }
                      variant="grey"
                      qaId="page-header-participate-dropdown-trigger"
                    >
                      {triggerText} <DropdownChevron />
                    </LayoutHeaderButton>
                  )}
                  trackClickedContext="Header Dropdown > Participate"
                />
              ) : (
                <LayoutHeaderButton
                  variant="grey"
                  href={IDEALIST_DAYS_PATH[locale]}
                  onClick={() =>
                    trackClicked('Idealist Days', { context: 'Header' })
                  }
                  type="link"
                >
                  Idealist Days
                </LayoutHeaderButton>
              )}

              <FindAJobDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <LayoutHeaderButton
                    data={
                      isDesktop
                        ? {
                            type: 'link',
                            href: triggerHref,
                            onClick: () => {
                              trackClicked('Find a Job', { context: 'Header' });
                            },
                          }
                        : { type: 'inert' }
                    }
                    variant="grey"
                    qaId="page-header-find-a-job-dropdown-trigger"
                  >
                    {triggerText} <DropdownChevron />
                  </LayoutHeaderButton>
                )}
                trackClickedContext="Header Dropdown > Find a Job"
              />

              {[I18nLocale.EN, I18nLocale.ES].includes(locale) ? (
                <VolunteerDropdown
                  renderTrigger={({ triggerText, triggerHref }) => (
                    <LayoutHeaderButton
                      variant="grey"
                      qaId="page-header-volunteer-dropdown-trigger"
                      data={
                        isDesktop
                          ? {
                              type: 'link',
                              href: triggerHref,
                              onClick: () => {
                                if (isDesktop) {
                                  trackClicked('Volunteer', {
                                    context: 'Header',
                                  });
                                }
                              },
                            }
                          : { type: 'inert' }
                      }
                    >
                      {triggerText} <DropdownChevron />
                    </LayoutHeaderButton>
                  )}
                  trackClickedContext="Header Dropdown > Volunteer"
                />
              ) : (
                <LayoutHeaderButton
                  type="link"
                  variant="grey"
                  qaId="nav-volop-link"
                  href={searchGetVolopsRoutePath({
                    locale,
                    embedSearchWidgetId: null,
                  })}
                  onClick={() =>
                    trackClicked('Volunteer', { context: 'Header' })
                  }
                >
                  {locale === I18nLocale.PT ? 'Voluntariar' : i18n('Volunteer')}
                </LayoutHeaderButton>
              )}

              <PostOnIdealistDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <LayoutHeaderButton
                    variant="grey"
                    qaId="page-header-post-on-idealist-dropdown-trigger"
                    data={
                      isDesktop
                        ? {
                            type: 'link',
                            href: triggerHref,
                            onClick: () => {
                              trackClicked('Post a Listing', {
                                context: 'Header',
                              });
                            },
                          }
                        : { type: 'inert' }
                    }
                  >
                    {triggerText} <DropdownChevron />
                  </LayoutHeaderButton>
                )}
                trackClickedContext="Header Dropdown > Post on Idealist"
              />

              {locale === I18nLocale.EN && (
                <LayoutHeaderApiAndIntegrationsDropdownHolder>
                  <ApiAndIntegrationsDropdown
                    renderTrigger={({ triggerText, triggerHref }) => (
                      <LayoutHeaderButton
                        data={
                          isDesktop
                            ? {
                                type: 'link',
                                href: triggerHref,
                                onClick: () => {
                                  trackClicked('API & Integrations', {
                                    context: 'Header',
                                  });
                                },
                              }
                            : { type: 'inert' }
                        }
                        variant="grey"
                        qaId="page-header-api-and-integrations-dropdown-trigger"
                      >
                        {triggerText} <DropdownChevron />
                      </LayoutHeaderButton>
                    )}
                    trackClickedContext="Header Dropdown > API & Integrations"
                  />
                </LayoutHeaderApiAndIntegrationsDropdownHolder>
              )}

              {user?.orgs.length === 0 && (
                <LayoutHeaderDesktopDonateLinkHolder>
                  <LayoutHeaderButton
                    variant="grey"
                    type="link"
                    href={DONATE_BUTTON_DETAILS[locale].href}
                    onClick={() =>
                      trackClicked('Donate', { context: 'Header' })
                    }
                  >
                    {DONATE_BUTTON_DETAILS[locale].text}
                  </LayoutHeaderButton>
                </LayoutHeaderDesktopDonateLinkHolder>
              )}
            </LayoutHeaderLinksHolder>
          </LayoutHeaderLeft>

          <LayoutHeaderRight>
            {isUserLoaded && !user && (
              <LayoutHeaderAuthHolder>
                <LayoutHeaderAuthLink
                  href={loginLink}
                  rel="nofollow"
                  onClick={(e) => {
                    trackClicked('Log In Header');
                    onClickLogin(e);
                  }}
                  data-qa-id="login-link"
                  aria-label={i18n('Log In')}
                >
                  {i18n('Log In')}
                </LayoutHeaderAuthLink>

                <LayoutHeaderSignupLinkHolder>
                  <LayoutHeaderAuthLink
                    href={authSignupRoute.with({})}
                    rel="nofollow"
                    onClick={(e) => {
                      trackClicked('Signup Header');

                      e.preventDefault();
                      showModal('SIGNUP', {});
                    }}
                    data-qa-id="signup-link"
                    aria-label={i18n('Sign Up')}
                  >
                    {i18n('Sign Up')}
                  </LayoutHeaderAuthLink>
                </LayoutHeaderSignupLinkHolder>
              </LayoutHeaderAuthHolder>
            )}

            <LayoutHeaderMobileSearchHolder>
              <Dropdown
                trigger={
                  <LayoutHeaderButton
                    type="inert"
                    ariaLabel={i18n('Toggle search')}
                    qaId="search-menu-button"
                    variant="grey"
                  >
                    <Icon name="search" size={24} />
                  </LayoutHeaderButton>
                }
                onToggle={(open) =>
                  open && trackClicked('Header Utility CTA - Search')
                }
              >
                <MobileSearchMenu
                  defaultSearchType={defaultSearchType}
                  initialQuery={initialQuery}
                  searchLocation={searchLocation}
                  onChangeSearchLocation={changeSearchLocation}
                  radiusOptions={radiusOptions}
                  radiusHandle={radiusHandle}
                  onSearch={search}
                  searchOnClear={searchOnClear}
                />
              </Dropdown>
            </LayoutHeaderMobileSearchHolder>

            {user && (
              <LayoutHeaderMyAccountHolder>
                <MyAccountDropdownContainerLoadable />
              </LayoutHeaderMyAccountHolder>
            )}

            {user && (
              <LayoutHeaderNotificationsDropdownHolder>
                <NotificationsDropdownContainerLoadable />
              </LayoutHeaderNotificationsDropdownHolder>
            )}

            {user && user.orgs.length > 0 && (
              <LayoutHeaderCartDropdownButtonHolder>
                <CartDropdownButtonContainer />
              </LayoutHeaderCartDropdownButtonHolder>
            )}
          </LayoutHeaderRight>
        </LayoutHeaderTopSectionHolder>

        <LayoutHeaderBottomSectionHolder
          $isInSearchPage={isSearchPage}
          $withScrolledTransition={isSearchPage && withScrolledTransition}
        >
          {showDesktopSearch && (
            <LayoutHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
              <DesktopSearchBar
                defaultSearchType={defaultSearchType}
                onSearch={search}
                searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
                locationInput={
                  <DesktopSearchBarLocationInput
                    searchLocation={searchLocation ?? null}
                    onChangeSearchLocation={changeSearchLocation}
                  />
                }
                radiusInput={
                  <LocationRadiusSelect
                    handle={radiusHandle}
                    options={radiusOptions}
                    disabled={
                      !searchLocation || !isPointGeotype(searchLocation.geoType)
                    }
                    qaId="page-header-desktop-location-radius"
                  />
                }
                queryInput={
                  <DesktopSearchBarQueryInput
                    initialQuery={initialQuery}
                    searchOnClear={searchOnClear}
                  />
                }
                submitButton={<DesktopSearchBarSubmitButton />}
              />
            </LayoutHeaderDesktopSearchHolder>
          )}

          {showSearchRefinements && (
            <SearchRefinementsSwitchLoadable searchType={defaultSearchType} />
          )}
          <FlashMessageContainer />

          <LayoutHeaderProgressBarHolder>
            <RouteLoadingProgressBar />
          </LayoutHeaderProgressBarHolder>
        </LayoutHeaderBottomSectionHolder>
      </LayoutHeaderInnerHolder>
    </LayoutHeaderHolder>
  );
}
